import { getCookieData, getSessionId, uuidV4 } from '@repo/common-utils';
import { useWorkingPlatform, MAINTENANCE_BYPASS_KEY } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useAtom, useSetAtom } from 'jotai';
import { useRouter, useSearchParams } from 'next/navigation';

import { requestChat } from '@/lib/api/chat';
import { checkMaintenance } from '@/lib/api/system';
import { CURRENT_CHAT_ID_KEY } from '@/lib/constants/storage-keys';
import { useSenaWebDb } from '@/lib/hooks/use-sena-web-db';
import {
  abortControllerAtom,
  currentChatIdAtom,
  currentQuestionAtom,
  isLoadingAtom,
  isStreamingAtom,
  loadedChatContentAtom,
  questionTextareaValueAtom
} from '@/lib/stores/chat';
import { ChatItemContent, RequestPayload } from '@/lib/types/content';
import { windowScrollToBottom } from '@/lib/utils/page-control';
import { convertMarkdownToHtml } from '@/lib/utils/strings';

export const useChatMessage = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const setTextareaValue = useSetAtom(questionTextareaValueAtom);
  const [loadedContent, setLoadedContent] = useAtom(loadedChatContentAtom);
  const [currentChatId, setCurrentChatId] = useAtom(currentChatIdAtom);
  const [currentQuestion, setCurrentQuestion] = useAtom(currentQuestionAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const [isStreaming, setIsStreaming] = useAtom(isStreamingAtom);
  const [abortController, setAbortController] = useAtom(abortControllerAtom);
  const { workingPlatform } = useWorkingPlatform();
  const senaWebDb = useSenaWebDb();
  const { sendCustomActionEvent } = useUserEventLogger();

  const generateNewChatId = () => {
    const newChatId: string = uuidV4();
    setCurrentChatId(newChatId);
    sessionStorage.setItem(CURRENT_CHAT_ID_KEY, newChatId);

    return newChatId;
  };

  const generateRequestData = (): RequestPayload => {
    if (!loadedContent) return [{ username: 'user', text: currentQuestion }];

    const result: RequestPayload = [];
    for (const content of loadedContent) {
      if (content.question) {
        result.push({ username: 'user', text: content.question });
      }
      if (content.answer && content.isComplete) {
        result.push({ username: 'bot', text: { ...content.answer, answer: content.answer.answerMarkdown ?? '' }, isComplete: content.isComplete });
      }
    }

    return result;
  };

  const updateLatestContent = (newContent: ChatItemContent) => {
    if (!loadedContent || loadedContent.length === 0) return;
    const sessionId = getSessionId();

    const updatedContent = loadedContent?.map((content, index) => {
      if (index === loadedContent.length - 1) {
        if (newContent.id < 0) newContent.id++;
        return newContent;
      }
      return content;
    });
    setLoadedContent(updatedContent);

    if (newContent.isComplete) {
      const newContent = updatedContent.filter((content) => content.isComplete);
      senaWebDb.setChatContent(sessionId, currentChatId, newContent);

      sendCustomActionEvent('chat_my-question', 'added', { 'my-question-no': newContent.filter((content) => content.isDirectRequest).length });
    }
  };

  const setNewQuestion = (question: string) => {
    const newContent: ChatItemContent = {
      id: -10000,
      question: question,
      answer: { answer: '', type: 'question' },
      isComplete: false,
      isDirectRequest: true
    };
    loadedContent?.push(newContent);
    setLoadedContent(loadedContent.slice());
    setTextareaValue('');
    setCurrentQuestion('');

    return newContent;
  };

  const startStreaming = async (newContent: ChatItemContent, chatResponse: Response) => {
    if (!chatResponse.body) return;
    setIsStreaming(true);

    const reader = chatResponse.body.getReader();
    while (true) {
      const { value, done } = await reader.read();
      setIsLoading(false);

      if (done) {
        if (!newContent.isComplete) {
          newContent.isComplete = true;
          updateLatestContent(newContent as ChatItemContent);
        }
        break;
      }

      // 한 청크에 여러 메시지가 올 수 있으므로 모든 라인을 순회
      const decodedChunk = new TextDecoder('utf-8').decode(value);
      const lines = decodedChunk.split('<ENDLINE>').filter((line) => line.trim());

      for (const line of lines) {
        const extractedString = line.replace(/data:/, '').trim();
        if (!extractedString) continue;

        try {
          const result = JSON.parse(extractedString);
          if (!result) continue;

          if (result.answer) {
            result.answerMarkdown = result.answer;
            result.answer = convertMarkdownToHtml(result.answerMarkdown);
            if ('qaHistoryId' in result) {
              newContent.id = result.qaHistoryId;
              newContent.isComplete = true;

              delete result.qaHistoryId;
            }

            newContent.answer = { ...result, type: 'question' };
            requestAnimationFrame(() => {
              updateLatestContent(newContent as ChatItemContent);
              requestAnimationFrame(() => windowScrollToBottom(true));
            });
          }
        } catch (error) {
          console.error('Error parsing JSON:', error);
          console.debug('Received line:', line);
          console.debug('Extracted string:', extractedString);
          continue; // 다음 라인 계속 처리
        }
      }

      if (newContent.isComplete) break;
    }
    setIsStreaming(false);
  };

  const sendMessage = async (question?: string) => {
    if (isLoading || isStreaming) return;
    setIsLoading(true);
    const newContent = setNewQuestion(question ?? currentQuestion);

    const maintenanceBypassValue = searchParams.get('zent') ?? getCookieData(MAINTENANCE_BYPASS_KEY) ?? '';
    if (maintenanceBypassValue !== 'admin') {
      const isMaintenance = await checkMaintenance();
      if (isMaintenance) {
        setIsLoading(false);
        router.push('/system-maintenance');
        return;
      }
    }

    try {
      const requestData = generateRequestData();
      const chatResponse = await requestChat(requestData, abortController, searchParams.get('chatMode') || '', workingPlatform);

      await startStreaming(newContent, chatResponse);
    } catch (error) {
      if (!abortController?.signal.aborted) {
        abortController?.abort('Request Aborted: Unexpected error occurred');
        console.error('Error during chat request:', error);
      }
    } finally {
      setIsStreaming(false);
      setIsLoading(false);
      setAbortController(new AbortController());
    }
  };

  const abortMessage = () => {
    if (abortController && !abortController.signal.aborted) {
      abortController.abort('User aborted');
      setAbortController(new AbortController());
    }
    setIsLoading(false);
    setIsStreaming(false);
    setCurrentQuestion('');
  };

  return { sendMessage, abortMessage, isLoading, isStreaming, currentQuestion, generateNewChatId };
};
